import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { AuthCard } from "@/components/auth-card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoginForm } from "@/features/auth/login/form";
import { m } from "@/lib/paraglide/messages.js";

const LoginSearch = z.object({
  // Where to go after sign-in: an app path only, never another site (an open redirect).
  redirect: z
    .string()
    .regex(/^\/(?!\/)/)
    .optional()
    .catch(undefined),
  // Better Auth's verification link lands here with ?verified=1, plus ?error= when it failed.
  verified: z.coerce.string().optional().catch(undefined),
  error: z.string().optional().catch(undefined),
  // ?password=reset: the reset-password page sent them here.
  password: z.literal("reset").optional().catch(undefined),
});

export const Route = createFileRoute("/(public)/login")({
  validateSearch: LoginSearch,
  component: LoginPage,
});

function LoginPage() {
  const { redirect, verified, error, password } = Route.useSearch();
  const router = useRouter();
  return (
    <AuthCard
      title={m.common_sign_in()}
      footer={
        <>
          {m.login_not_a_reseller()} <Link to="/apply">{m.login_apply()}</Link>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {password && (
          <Alert>
            <AlertDescription>{m.login_password_reset()}</AlertDescription>
          </Alert>
        )}
        {verified && !error && (
          <Alert>
            <AlertDescription>{m.login_verified()}</AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{m.login_verification_link_invalid()}</AlertDescription>
          </Alert>
        )}
        <LoginForm
          onSignedIn={() => router.history.push(redirect ?? "/dashboard")}
          forgotPassword={
            // login-03's "Forgot your password?" link, as shipped.
            <Link to="/forgot-password" className="ml-auto text-sm underline-offset-4 hover:underline">
              {m.login_forgot_password()}
            </Link>
          }
        />
      </div>
    </AuthCard>
  );
}
